import React, { useCallback } from 'react';

import { IPersonaFront } from '@api/routers/user/service';
import styled from '@emotion/styled';
import { Blockchain } from '@repo/prsn-blockchain';
import { MutationStatus } from '@tanstack/react-query';
import { NamespaceKeys, useTranslations } from 'next-intl';

import { READ_RECEIPT_TIMEOUT } from '@web/features/staking/utils';
import { Button } from '@web/ui/components';
import { IModalProps } from '@web/ui/components/modal';
import { env } from '@web/utils/env';
import { clients } from '@web/utils/rainbowkit/chains';

import { useApprovalForAll } from '../hooks';

import { BaseStakingModal } from './BaseStakingModal';
import { StakingAlert } from './components/StakingAlert';
import { StakingStep } from './components/StakingStep';

const Action = styled(Button)`
  white-space: nowrap;
`;

const Group = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('0250')};
`;

export interface IBridgeModalProps extends IModalProps {
  personas: Pick<IPersonaFront, 'tokenId' | 'blockchain'>[];
  stake?: boolean;
  bridge: () => Promise<void>;
  status: MutationStatus;
}

export const BridgeModal = ({
  personas,
  stake,
  bridge,
  status,
  ...props
}: IBridgeModalProps) => {
  const t = useTranslations();

  const { mutation: approvalMutation } = useApprovalForAll();

  const currentBlockchain = personas[0]?.blockchain;

  const destination = currentBlockchain
    ? currentBlockchain === Blockchain.BASE
      ? Blockchain.ETHEREUM
      : Blockchain.BASE
    : undefined;

  const handleBridge = useCallback(async () => {
    if (!currentBlockchain) {
      return;
    }

    if (currentBlockchain === Blockchain.ETHEREUM) {
      const approvalHash = await approvalMutation.mutateAsync({
        blockchain: currentBlockchain,
        operator: env.ethereum.BRIDGE_ADDRESS,
      });
      if (approvalHash) {
        await clients.ethereum.waitForTransactionReceipt({
          hash: approvalHash,
          timeout: READ_RECEIPT_TIMEOUT,
        });
      }
    }

    await bridge();
  }, [personas, bridge]);

  if (!destination) return null;

  const tKeys = (() => {
    if (currentBlockchain === Blockchain.ETHEREUM && stake) {
      return {
        title: `STAKE_MODAL_TITLE_ETHEREUM`,
        subtitle: 'STAKE_MODAL_SUBTITLE',
        completed: 'STAKE_MODAL_COMPLETED_TEXT',
        confirm: 'STAKE_MODAL_ACTION_CONFIRM_ETHEREUM',
        cancel: 'STAKE_MODAL_ACTION_CANCEL',
      } as const;
    }

    return {
      title: `BRIDGE_MODAL_TITLE`,
      subtitle: 'BRIDGE_MODAL_SUBTITLE',
      completed: 'BRIDGE_MODAL_COMPLETED_TEXT',
      confirm: 'BRIDGE_MODAL_ACTION_CONFIRM',
      cancel: 'BRIDGE_MODAL_ACTION_CANCEL',
    } as const;
  })();

  return (
    <BaseStakingModal
      personas={personas}
      status={status}
      title={t(tKeys.title, { personas: personas.length })}
      subtitle={t(tKeys.subtitle)}
      completedText={t(tKeys.completed)}
      actions={[
        <Action label={t(tKeys.confirm)} onClick={handleBridge} fullWidth />,
        <Action
          label={t(tKeys.cancel)}
          onClick={props.onClose}
          variant="secondary"
          color="onSurface"
          background="surface"
        />,
      ]}
      {...props}
    >
      {status === 'error' && (
        <StakingAlert>{t('STAKING_ALERT_ERROR')}</StakingAlert>
      )}
      <Group>
        <StakingStep
          title={t(
            `STAKING_STEP_BRIDGE_TITLE_${destination.toUpperCase()}` as NamespaceKeys<
              string,
              string
            >,
          )}
          description={t(
            `STAKING_STEP_BRIDGE_DESCRIPTION_${destination.toUpperCase()}` as NamespaceKeys<
              string,
              string
            >,
            {
              personas: personas.length,
            },
          )}
          status={status}
          type={destination}
        />
        {currentBlockchain === Blockchain.ETHEREUM && stake && (
          <StakingStep
            title={t('STAKING_STEP_STAKE_TITLE')}
            description={t('STAKING_STEP_STAKE_DESCRIPTION')}
            status={status === 'success' ? 'success' : 'idle'}
            type="stake"
          />
        )}
      </Group>
    </BaseStakingModal>
  );
};
